import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { HelpTag } from "routes/help/helpTag"
import { Box, Grid, IconButton, ImageList, ImageListItem, Link, List, Stack, Typography } from "@mui/material"
import { useParams } from "react-router-dom"
import { useMemo } from "react"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useDialog } from "lib/@hooks/useDialog"
import { EditAssetDialog } from "routes/regime/assets/edit-asset-dialog"
import { updateAssetRegister } from "routes/asset-register/controller/update-asset-register"
import { Bound } from "lib/@components/binding/Bound"
import { assetRegister } from "routes/asset-register/controller/asset-register"
import { navigate } from "lib/routes/navigate"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { useRefresh } from "lib/@hooks/useRefresh"
import { assetDefinition } from "routes/asset-register/controller/asset-definition"
import { omit } from "lib/omit"
import { Secure } from "lib/authorization/secure"
import { Repeat } from "lib/@components/repeat"
import { getRolloutAssetFields } from "routes/asset-register/plugins/asset-mapping/rollout-asset-fields"
import { AssetDisplayField } from "routes/asset-register/asset-display-field"
import Iconify from "minimals-template/components/Iconify"
import { HELPTAG_ASSET } from "routes/help/help-tag-constants"
import { If } from "lib/@components/switch"
import { regimesForAsset } from "routes/asset-register/controller/regimes-for-asset"
import { RegimeEntry } from "routes/asset-register/regime-entry"
import { getPublishedPlan } from "routes/plan/controller/publish-plan"
import { RegimeDraftLockTooltip } from "routes/regime/regime-draft-locking/regime-draft-lock-tooltip"

export function Asset() {
    const { id, assetId } = useParams()
    const refresh = useRefresh()
    const definition = assetDefinition.useResults(assetId)
    const register = assetRegister.useResultsOnce(id ?? definition?.assetRegister) ?? []
    const createAssetDialog = useDialog(<EditAssetDialog register={id} source={definition} />, { maxWidth: "md" })
    const regimes = regimesForAsset.useResults(assetId)
    const { share, regime: regimeId } = Object.fromEntries(new URLSearchParams(window.location.search).entries())
    const baseRegimeId = regimeId?.split("|").at(-1)
    const publishedRegime = getPublishedPlan.useResults(baseRegimeId, "publish")
    const isRegimeDraftLocked = !!publishedRegime?.tree
    const assetFields = useMemo(getRolloutAssetFields, [])

    return (
        <Bound refresh={refresh} target={definition}>
            <PageBreadcrumbContainer
                parentCrumbs={[
                    <Link key="root" sx={{ cursor: "pointer" }} onClick={go}>
                        Asset Registers
                    </Link>,
                    <Link key={register?.name ?? "register"} sx={{ cursor: "pointer" }} onClick={goRegister}>
                        {register?.name ?? ""}
                    </Link>,
                ]}
                title={`${register?.name ?? ""}: ${definition?.tag}` ?? ""}
                sx={{ height: 1 }}
                hideCrumbs={!!share}
            >
                <HelpTag tags={[HELPTAG_ASSET]} />
                <Stack spacing={1} sx={{ mt: 2, flex: 1 }}>
                    <Secure demand="$professional-services" hide>
                        <ListItemBox spacing={1}>
                            <Box flex={1} />
                            <Bound isRegimeDraftLocked={isRegimeDraftLocked}>
                                <RegimeDraftLockTooltip>
                                    <IconButton
                                        aria-label="Edit asset"
                                        color="primary"
                                        onClick={editAsset}
                                        data-cy="edit-asset"
                                    >
                                        <Iconify icon="mdi:settings" />
                                    </IconButton>
                                </RegimeDraftLockTooltip>
                            </Bound>
                        </ListItemBox>
                    </Secure>
                    <Grid container spacing={2}>
                        <Grid item md={6} xs={12}>
                            <Stack spacing={1} sx={{ pt: 2 }} data-cy="asset-tag-column">
                                <BoundTextField
                                    variant="filled"
                                    InputProps={{ disableUnderline: true, readOnly: true }}
                                    field="tag"
                                />
                                <Repeat list={assetFields} item={<AssetDisplayField />} />
                            </Stack>
                        </Grid>
                        <Grid item md={6} xs={12}>
                            <Stack spacing={1} sx={{ pt: 2 }} data-cy="asset-type-column">
                                <BoundTextField
                                    variant="filled"
                                    InputProps={{ disableUnderline: true, readOnly: true }}
                                    field="type"
                                />
                                {!!definition?.name && (
                                    <BoundTextField
                                        size="small"
                                        variant="standard"
                                        InputProps={{ disableUnderline: true, readOnly: true }}
                                        field="name"
                                    />
                                )}
                                {!!definition?.definition && (
                                    <BoundTextField
                                        size="small"
                                        variant="standard"
                                        InputProps={{ disableUnderline: true, readOnly: true }}
                                        field="definition"
                                    />
                                )}
                                {!!definition?.barcode && (
                                    <BoundTextField
                                        size="small"
                                        variant="standard"
                                        InputProps={{ disableUnderline: true, readOnly: true }}
                                        field="barcode"
                                    />
                                )}
                                {!!definition?.code && (
                                    <BoundTextField
                                        size="small"
                                        variant="standard"
                                        InputProps={{ disableUnderline: true, readOnly: true }}
                                        field="code"
                                    />
                                )}
                                {!!definition?.serialNumber && (
                                    <BoundTextField
                                        size="small"
                                        variant="standard"
                                        InputProps={{ disableUnderline: true, readOnly: true }}
                                        field="serialNumber"
                                    />
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12}>
                            <If truthy={regimes?.length && !share}>
                                <Typography variant="h5">Usage</Typography>
                                <List data-cy="asset-usage-list">
                                    <Repeat list={regimes} item={<RegimeEntry />} />
                                </List>
                            </If>
                        </Grid>
                        <Grid item xs={12}>
                            <Box height={64} />
                            <ImageList variant="masonry" cols={2} gap={2}>
                                {(definition?.picture?.documents ?? []).map(({ href }) => (
                                    <ImageListItem key={href}>
                                        <img alt="Asset" src={href} />
                                    </ImageListItem>
                                ))}
                            </ImageList>
                            <Box height={64} />
                        </Grid>
                    </Grid>
                </Stack>
            </PageBreadcrumbContainer>
        </Bound>
    )

    function goRegister() {
        navigate(`/app/assets/${register.id}`)
    }

    async function editAsset() {
        const update = await createAssetDialog()
        if (update) {
            await updateAssetRegister({
                registerId: register.id,
                updateAssets: [omit(update, "modified", "__typename")],
            })
        }
    }

    function go() {
        navigate("/app/assets")
    }
}
